// m.45 Pošiljke, tab Pristigle: dolazne pošiljke u sefu do primke u Farosu.
import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate, downloadFile, uploadFile, ApiError } from '../shared/api';
import { DateInput } from '../shared/DateInput';
import { formatDate, formatDateTime, formatNumber } from '../shared/formatting';
import { Btn, Pill } from '../shared/ui';

type Locale = 'hr' | 'en';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const inp: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem', width: '100%', boxSizing: 'border-box' };
const th: React.CSSProperties = { textAlign: 'left', padding: '7px 9px', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--ad-ink)', borderBottom: '2px solid #efe6d2', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '6px 9px', fontSize: '0.83rem', borderBottom: '1px solid #f3ecdb', verticalAlign: 'top' };
const lbl: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 600, color: '#6b5b34', marginBottom: 3, display: 'block' };

type Status = 'pristiglo' | 'u_pregledu' | 'zaprimljeno' | 'vraceno';

const STATUS: Record<Status, { hr: string; en: string; tone: 'ok' | 'warn' | 'bad' | 'info' | 'neutral' }> = {
    pristiglo: { hr: 'Pristiglo', en: 'Arrived', tone: 'warn' },
    u_pregledu: { hr: 'U pregledu', en: 'In review', tone: 'info' },
    zaprimljeno: { hr: 'Zaprimljeno', en: 'Received', tone: 'ok' },
    vraceno: { hr: 'Vraćeno', en: 'Returned', tone: 'neutral' },
};

type Row = {
    id: number; arrived_at: string; carrier: string; tracking_no: string | null; gross_g: number | null;
    origin_country: string | null; origin_city: string | null; sender: string | null; kind: string;
    content_desc: string | null; delivery_note_no: string | null; branch_code: string | null; safe_id: number | null;
    safe_name: string | null; received_by_name: string | null; status: Status; primka_date: string | null;
    primka_no: string | null; warehouse_code: string | null; net_g: number | null; fine_g: number | null;
    import_order_id: number | null; gtm_delivery_note_id: number | null; has_attachment: boolean; note: string | null;
    days_in_safe: number | null; overdue: boolean;
};

type Meta = {
    carriers: string[];
    kinds: Array<{ key: string; label: string }>;
    statuses: Array<{ key: Status; label: string }>;
    transitions: Record<Status, Status[]>;
    branches: Array<{ code: string; name: string }>;
    warehouses: Array<{ code: string; name: string }>;
    safes: Array<{ id: number; name: string; branch_code: string | null }>;
    settings: { threshold_days: number; recipients: string[]; carriers: string[] };
    can_edit: boolean;
    is_superadmin: boolean;
};

type Form = {
    id?: number; arrived_at: string; carrier: string; tracking_no: string; gross: string; origin_country: string;
    origin_city: string; sender: string; kind: string; content_desc: string; delivery_note_no: string;
    branch_code: string; safe_id: string; note: string;
};

type Receive = { id: number; primka_date: string; primka_no: string; warehouse_code: string; net_g: string; fine_g: string };

type Event = { event: string; old_status: string | null; new_status: string | null; by_name: string | null; detail: string | null; at: string | null };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return <div style={{ marginBottom: 10 }}><span style={lbl}>{label}</span>{children}</div>;
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(30,20,5,0.45)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', overflowY: 'auto' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: wide ? 820 : 520, maxWidth: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, color: 'var(--ad-ink)', fontSize: '1.05rem' }}>{title}</h3>
                    <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: '1.3rem', cursor: 'pointer', color: '#8a7a52' }}>×</button>
                </div>
                {children}
            </div>
        </div>
    );
}

const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = (carrier: string): Form => ({
    arrived_at: todayIso(), carrier, tracking_no: '', gross: '', origin_country: '', origin_city: '', sender: '',
    kind: 'roba', content_desc: '', delivery_note_no: '', branch_code: '', safe_id: '', note: '',
});

function errText(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
}

export function InboundShipmentsTab({ locale, token }: { locale: Locale; token: string | null }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [meta, setMeta] = useState<Meta | null>(null);
    const [rows, setRows] = useState<Row[]>([]);
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [threshold, setThreshold] = useState(3);
    const [f, setF] = useState({ status: '', carrier: '', kind: '', branch_code: '', from: '', to: '', min_days: '', q: '' });
    const [form, setForm] = useState<Form | null>(null);
    const [events, setEvents] = useState<Event[]>([]);
    const [receive, setReceive] = useState<Receive | null>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [sDraft, setSDraft] = useState({ threshold_days: '3', recipients: '', carriers: '' });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [info, setInfo] = useState('');

    const qs = useCallback(() => {
        const p = new URLSearchParams();
        Object.entries(f).forEach(([k, v]) => { if (v !== '') p.set(k, v); });
        return p.toString();
    }, [f]);

    const load = useCallback(async () => {
        const r = await api<{ rows: Row[]; counts: Record<string, number>; threshold_days: number }>(`/parcels/inbound?${qs()}`, token);
        setRows(r.rows); setCounts(r.counts); setThreshold(r.threshold_days);
    }, [token, qs]);

    const loadMeta = useCallback(async () => {
        const m = await api<Meta>('/parcels/inbound/meta', token);
        setMeta(m);
        setSDraft({ threshold_days: String(m.settings.threshold_days), recipients: m.settings.recipients.join(', '), carriers: m.settings.carriers.join(', ') });
    }, [token]);

    useEffect(() => { loadMeta().catch((e) => setErr(errText(e))); }, [loadMeta]);
    useEffect(() => { load().catch((e) => setErr(errText(e))); }, [load]);

    const canEdit = meta?.can_edit ?? false;
    const setFilter = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
    const setField = (k: keyof Form, v: string) => setForm((p) => (p ? { ...p, [k]: v } : p));

    async function openEdit(r: Row) {
        setErr('');
        setForm({
            id: r.id, arrived_at: r.arrived_at, carrier: r.carrier, tracking_no: r.tracking_no ?? '',
            gross: r.gross_g !== null ? String(r.gross_g).replace('.', ',') : '', origin_country: r.origin_country ?? '',
            origin_city: r.origin_city ?? '', sender: r.sender ?? '', kind: r.kind, content_desc: r.content_desc ?? '',
            delivery_note_no: r.delivery_note_no ?? '', branch_code: r.branch_code ?? '', safe_id: r.safe_id ? String(r.safe_id) : '', note: r.note ?? '',
        });
        try {
            const ev = await api<{ events: Event[] }>(`/parcels/inbound/${r.id}`, token);
            setEvents(ev.events);
        } catch { setEvents([]); }
    }

    async function save() {
        if (!form) return;
        setBusy(true); setErr('');
        try {
            const body: Record<string, unknown> = {
                arrived_at: form.arrived_at || null, carrier: form.carrier, tracking_no: form.tracking_no, gross: form.gross,
                origin_country: form.origin_country, origin_city: form.origin_city, sender: form.sender, kind: form.kind,
                content_desc: form.content_desc, delivery_note_no: form.delivery_note_no, branch_code: form.branch_code,
                safe_id: form.safe_id ? Number(form.safe_id) : null, note: form.note,
            };
            if (form.id) await mutate(`/parcels/inbound/${form.id}`, token, 'PATCH', body);
            else await mutate('/parcels/inbound', token, 'POST', body);
            setForm(null); await load();
        } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
    }

    async function setStatus(r: Row, status: Status) {
        const ask: Partial<Record<Status, string>> = {
            vraceno: t('Označiti pošiljku kao vraćenu pošiljatelju?', 'Mark shipment as returned to sender?'),
            pristiglo: t('Vratiti pošiljku u status Pristiglo?', 'Move shipment back to Arrived?'),
        };
        if (ask[status] && !window.confirm(ask[status])) return;
        setErr('');
        try {
            await mutate(`/parcels/inbound/${r.id}/status`, token, 'POST', { status });
            await load();
        } catch (e) { setErr(errText(e)); }
    }

    async function doReceive() {
        if (!receive) return;
        setBusy(true); setErr('');
        try {
            await mutate(`/parcels/inbound/${receive.id}/status`, token, 'POST', {
                status: 'zaprimljeno', primka_date: receive.primka_date, primka_no: receive.primka_no,
                warehouse_code: receive.warehouse_code || null, net_g: receive.net_g || null, fine_g: receive.fine_g || null,
            });
            setReceive(null); await load();
        } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
    }

    async function makeGtm(r: Row) {
        setErr(''); setInfo('');
        try {
            const res = await mutate<{ ok: boolean; note_id: number }>(`/gtm-scrap/notes/from-inbound/${r.id}`, token, 'POST');
            setInfo(t(`Napravljena stavka GTM br. ${res.note_id}.`, `GTM item ${res.note_id} created.`));
            await load();
        } catch (e) {
            if (e instanceof ApiError && e.status === 404) setErr(t('Evidencija loma GTM još nije dostupna.', 'GTM scrap register is not available yet.'));
            else setErr(errText(e));
        }
    }

    async function upload(file: File) {
        if (!form?.id) return;
        const fd = new FormData(); fd.append('file', file);
        setBusy(true); setErr('');
        try {
            await uploadFile(`/parcels/inbound/${form.id}/attachment`, token, fd);
            setInfo(t('Privitak spremljen.', 'Attachment saved.'));
            await load();
        } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
    }

    async function saveSettings() {
        setBusy(true); setErr('');
        try {
            const split = (s: string) => s.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);
            await mutate('/parcels/inbound/settings', token, 'POST', {
                threshold_days: Number(sDraft.threshold_days), recipients: split(sDraft.recipients), carriers: split(sDraft.carriers),
            });
            setSettingsOpen(false); await loadMeta(); await load();
        } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
    }

    const from = (r: Row) => [r.sender, r.origin_city, r.origin_country].filter(Boolean).join(', ');
    const kindLabel = (k: string) => meta?.kinds.find((x) => x.key === k)?.label ?? k;
    const allowed = (s: Status) => meta?.transitions[s] ?? [];
    const editRow = rows.find((r) => r.id === form?.id);
    const safes = (meta?.safes ?? []).filter((s) => !form?.branch_code || !s.branch_code || s.branch_code === form.branch_code);

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <select style={{ ...inp, width: 170 }} value={f.status} onChange={(e) => setFilter('status', e.target.value)}>
                    <option value="">{t('Pristiglo i u pregledu', 'Arrived and in review')}</option>
                    {(Object.keys(STATUS) as Status[]).map((k) => <option key={k} value={k}>{t(STATUS[k].hr, STATUS[k].en)}</option>)}
                    <option value="sve">{t('Svi statusi', 'All statuses')}</option>
                </select>
                <select style={{ ...inp, width: 140 }} value={f.carrier} onChange={(e) => setFilter('carrier', e.target.value)}>
                    <option value="">{t('Svi dostavljači', 'All carriers')}</option>
                    {(meta?.carriers ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select style={{ ...inp, width: 150 }} value={f.kind} onChange={(e) => setFilter('kind', e.target.value)}>
                    <option value="">{t('Sve vrste', 'All kinds')}</option>
                    {(meta?.kinds ?? []).map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
                <select style={{ ...inp, width: 170 }} value={f.branch_code} onChange={(e) => setFilter('branch_code', e.target.value)}>
                    <option value="">{t('Sve poslovnice', 'All branches')}</option>
                    {(meta?.branches ?? []).map((b) => <option key={b.code} value={b.code}>{b.code} {b.name}</option>)}
                </select>
                <div style={{ width: 130 }}><DateInput value={f.from} onChange={(v) => setFilter('from', v)} placeholder={t('od', 'from')} /></div>
                <div style={{ width: 130 }}><DateInput value={f.to} onChange={(v) => setFilter('to', v)} placeholder={t('do', 'to')} /></div>
                <input style={{ ...inp, width: 110 }} type="number" min={0} placeholder={t('min. dana', 'min. days')} value={f.min_days} onChange={(e) => setFilter('min_days', e.target.value)} />
                <input style={{ ...inp, width: 220 }} placeholder={t('Broj pošiljke, pošiljatelj, opis', 'Tracking, sender, description')} value={f.q} onChange={(e) => setFilter('q', e.target.value)} />
                <div style={{ flex: 1 }} />
                {canEdit ? <Btn variant="primary" onClick={() => { setErr(''); setEvents([]); setForm(emptyForm(meta?.carriers[0] ?? 'DHL')); }}>+ {t('Nova pošiljka', 'New shipment')}</Btn> : null}
                <Btn onClick={() => { downloadFile(`/parcels/inbound/export.xlsx?${qs()}`, token, 'pristigle_posiljke.xlsx').catch((e) => setErr(errText(e))); }}>{t('Izvoz XLSX', 'Export XLSX')}</Btn>
                {meta?.is_superadmin ? <Btn onClick={() => setSettingsOpen(true)}>{t('Postavke', 'Settings')}</Btn> : null}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                {(Object.keys(STATUS) as Status[]).map((k) => (
                    <div key={k} style={{ ...card, padding: '10px 14px', minWidth: 110, cursor: 'pointer' }} onClick={() => setFilter('status', k)}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#6b5b34' }}>{counts[k] ?? 0}</div>
                        <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{t(STATUS[k].hr, STATUS[k].en)}</div>
                    </div>
                ))}
                <div style={{ ...card, padding: '10px 14px', minWidth: 150 }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#c0392b' }}>{rows.filter((r) => r.overdue).length}</div>
                    <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{t(`Preko ${threshold} radna dana`, `Over ${threshold} working days`)}</div>
                </div>
            </div>

            {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
            {info ? <div style={{ color: '#2f855a', marginBottom: 8 }}>{info}</div> : null}

            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1100 }}>
                    <thead><tr>
                        {[t('Datum pristizanja', 'Arrived'), t('Dostavljač', 'Carrier'), t('Broj pošiljke', 'Tracking'), t('Bruto g', 'Gross g'), t('Pošiljka iz', 'From'), t('Vrsta', 'Kind'), t('Opis', 'Description'), t('Mjesto čuvanja', 'Storage'), t('Dana u sefu', 'Days in safe'), 'Status', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} style={r.overdue ? { background: '#fdf1ee' } : undefined}>
                                <td style={td}>{formatDate(r.arrived_at, locale)}</td>
                                <td style={td}>{r.carrier}</td>
                                <td style={td}>{r.tracking_no ?? '-'}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{r.gross_g !== null ? formatNumber(r.gross_g, locale) : '-'}</td>
                                <td style={td}>{from(r) || '-'}</td>
                                <td style={td}>{kindLabel(r.kind)}</td>
                                <td style={td}>{r.content_desc ?? ''}{r.delivery_note_no ? <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{t('Otpremnica', 'Delivery note')} {r.delivery_note_no}</div> : null}</td>
                                <td style={td}>{r.safe_name ?? r.branch_code ?? '-'}</td>
                                <td style={{ ...td, textAlign: 'right', fontWeight: r.overdue ? 700 : 400, color: r.overdue ? '#c0392b' : undefined }}>
                                    {r.days_in_safe ?? '-'}{r.overdue ? ' !' : ''}
                                </td>
                                <td style={td}>
                                    <Pill tone={STATUS[r.status]?.tone ?? 'neutral'}>{t(STATUS[r.status]?.hr ?? r.status, STATUS[r.status]?.en ?? r.status)}</Pill>
                                    {r.primka_no ? <div style={{ fontSize: '0.72rem', color: '#8a7a52' }}>{t('Primka', 'Receipt')} {r.primka_no}</div> : null}
                                </td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                        <Btn size="sm" onClick={() => void openEdit(r)}>{canEdit ? t('Uredi', 'Edit') : t('Detalji', 'Details')}</Btn>
                                        {canEdit && allowed(r.status).includes('u_pregledu') ? <Btn size="sm" onClick={() => void setStatus(r, 'u_pregledu')}>{t('U pregledu', 'In review')}</Btn> : null}
                                        {canEdit && allowed(r.status).includes('zaprimljeno') ? (
                                            <Btn size="sm" variant="primary" onClick={() => setReceive({ id: r.id, primka_date: todayIso(), primka_no: '', warehouse_code: r.warehouse_code ?? r.branch_code ?? '', net_g: '', fine_g: '' })}>{t('Zaprimi', 'Receive')}</Btn>
                                        ) : null}
                                        {canEdit && allowed(r.status).includes('vraceno') ? <Btn size="sm" onClick={() => void setStatus(r, 'vraceno')}>{t('Vrati', 'Return')}</Btn> : null}
                                        {meta?.is_superadmin && (r.status === 'zaprimljeno' || r.status === 'vraceno') ? <Btn size="sm" onClick={() => void setStatus(r, 'pristiglo')}>{t('Poništi', 'Undo')}</Btn> : null}
                                        {canEdit && r.kind === 'lom' && !r.gtm_delivery_note_id ? <Btn size="sm" variant="soft" onClick={() => void makeGtm(r)}>{t('Napravi stavku GTM', 'Create GTM item')}</Btn> : null}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 ? <tr><td style={td} colSpan={11}>{t('Nema pošiljaka.', 'No shipments.')}</td></tr> : null}
                    </tbody>
                </table>
            </div>

            {form ? (
                <Modal wide title={form.id ? t('Pristigla pošiljka', 'Inbound shipment') : t('Nova pristigla pošiljka', 'New inbound shipment')} onClose={() => setForm(null)}>
                    <fieldset disabled={!canEdit} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                            <Field label={t('Datum pristizanja', 'Arrival date')}><DateInput value={form.arrived_at} onChange={(v) => setField('arrived_at', v)} /></Field>
                            <Field label={t('Dostavljač', 'Carrier')}>
                                <select style={inp} value={form.carrier} onChange={(e) => setField('carrier', e.target.value)}>
                                    {(meta?.carriers ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </Field>
                            <Field label={t('Broj pošiljke', 'Tracking number')}><input style={inp} value={form.tracking_no} onChange={(e) => setField('tracking_no', e.target.value)} /></Field>
                            <Field label={t('Bruto masa (npr. 0,5 kg ili 500 g)', 'Gross mass (e.g. 0.5 kg or 500 g)')}><input style={inp} value={form.gross} onChange={(e) => setField('gross', e.target.value)} /></Field>
                            <Field label={t('Vrsta', 'Kind')}>
                                <select style={inp} value={form.kind} onChange={(e) => setField('kind', e.target.value)}>
                                    {(meta?.kinds ?? []).map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                                </select>
                            </Field>
                            <Field label={t('Broj otpremnice pošiljatelja', 'Sender delivery note no.')}><input style={inp} value={form.delivery_note_no} onChange={(e) => setField('delivery_note_no', e.target.value)} /></Field>
                            <Field label={t('Država', 'Country')}><input style={inp} value={form.origin_country} onChange={(e) => setField('origin_country', e.target.value)} /></Field>
                            <Field label={t('Grad', 'City')}><input style={inp} value={form.origin_city} onChange={(e) => setField('origin_city', e.target.value)} /></Field>
                            <Field label={t('Pošiljatelj', 'Sender')}><input style={inp} value={form.sender} onChange={(e) => setField('sender', e.target.value)} /></Field>
                            <Field label={t('Poslovnica', 'Branch')}>
                                <select style={inp} value={form.branch_code} onChange={(e) => setField('branch_code', e.target.value)}>
                                    <option value="">-</option>
                                    {(meta?.branches ?? []).map((b) => <option key={b.code} value={b.code}>{b.code} {b.name}</option>)}
                                </select>
                            </Field>
                            <Field label={t('Mjesto čuvanja (sef)', 'Storage (safe)')}>
                                <select style={inp} value={form.safe_id} onChange={(e) => setField('safe_id', e.target.value)}>
                                    <option value="">-</option>
                                    {safes.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                                </select>
                            </Field>
                        </div>
                        <Field label={t('Opis sadržaja', 'Content description')}><input style={inp} value={form.content_desc} onChange={(e) => setField('content_desc', e.target.value)} /></Field>
                        <Field label={t('Napomena', 'Note')}><textarea style={{ ...inp, minHeight: 44 }} value={form.note} onChange={(e) => setField('note', e.target.value)} /></Field>
                    </fieldset>

                    {editRow ? (
                        <div style={{ fontSize: '0.78rem', color: '#6b5b34', marginBottom: 10 }}>
                            {t('Zaprimio', 'Registered by')} {editRow.received_by_name ?? '-'}
                            {editRow.primka_no ? ` · ${t('Primka', 'Receipt')} ${editRow.primka_no} ${editRow.primka_date ? formatDate(editRow.primka_date, locale) : ''} · ${editRow.warehouse_code ?? ''}` : ''}
                            {editRow.net_g !== null ? ` · ${t('neto', 'net')} ${formatNumber(editRow.net_g, locale)} g` : ''}
                            {editRow.fine_g !== null ? ` · fine ${formatNumber(editRow.fine_g, locale)} g` : ''}
                        </div>
                    ) : null}

                    {form.id ? (
                        <Field label={t('Privitak (fotografija ili PDF, do 10 MB)', 'Attachment (photo or PDF, max 10 MB)')}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                {canEdit ? <input type="file" accept="application/pdf,image/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) void upload(file); }} /> : null}
                                {editRow?.has_attachment ? (
                                    <a href="#" onClick={(ev) => { ev.preventDefault(); downloadFile(`/parcels/inbound/${form.id}/attachment`, token, `posiljka_${form.id}`).catch((e) => setErr(errText(e))); }}>{t('Preuzmi privitak', 'Download attachment')}</a>
                                ) : null}
                            </div>
                        </Field>
                    ) : null}

                    {events.length ? (
                        <div style={{ background: '#faf6ec', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#6b5b34', marginBottom: 6 }}>{t('Povijest', 'History')}</div>
                            {events.map((ev, i) => (
                                <div key={i} style={{ fontSize: '0.76rem', color: '#6b5b34', marginBottom: 2 }}>
                                    {ev.at ? formatDateTime(ev.at, locale) : ''} · {ev.by_name ?? 'BIS'} · {ev.event === 'status' ? `${ev.old_status ?? ''} -> ${ev.new_status ?? ''}` : ev.event}{ev.detail ? ` · ${ev.detail}` : ''}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                        {form.id && canEdit ? (
                            <Btn variant="danger" onClick={() => {
                                if (!window.confirm(t('Obrisati pošiljku?', 'Delete shipment?'))) return;
                                mutate(`/parcels/inbound/${form.id}`, token, 'DELETE').then(() => { setForm(null); return load(); }).catch((e) => setErr(errText(e)));
                            }}>{t('Obriši', 'Delete')}</Btn>
                        ) : <span />}
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn onClick={() => setForm(null)}>{t('Zatvori', 'Close')}</Btn>
                            {canEdit ? <Btn variant="primary" disabled={busy} onClick={() => void save()}>{busy ? t('Spremam...', 'Saving...') : t('Spremi', 'Save')}</Btn> : null}
                        </div>
                    </div>
                </Modal>
            ) : null}

            {receive ? (
                <Modal title={t('Zaprimi pošiljku', 'Receive shipment')} onClose={() => setReceive(null)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Field label={t('Datum primke', 'Receipt date')}><DateInput value={receive.primka_date} onChange={(v) => setReceive({ ...receive, primka_date: v })} /></Field>
                        <Field label={t('Broj primke', 'Receipt number')}><input style={inp} value={receive.primka_no} onChange={(e) => setReceive({ ...receive, primka_no: e.target.value })} /></Field>
                        <Field label={t('Skladište', 'Warehouse')}>
                            <select style={inp} value={receive.warehouse_code} onChange={(e) => setReceive({ ...receive, warehouse_code: e.target.value })}>
                                <option value="">-</option>
                                {(meta?.warehouses ?? []).map((w) => <option key={w.code} value={w.code}>{w.code} {w.name}</option>)}
                            </select>
                        </Field>
                        <div />
                        <Field label={t('Neto masa (g)', 'Net mass (g)')}><input style={inp} value={receive.net_g} onChange={(e) => setReceive({ ...receive, net_g: e.target.value })} /></Field>
                        <Field label={t('Fine masa (g)', 'Fine mass (g)')}><input style={inp} value={receive.fine_g} onChange={(e) => setReceive({ ...receive, fine_g: e.target.value })} /></Field>
                    </div>
                    {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Btn onClick={() => setReceive(null)}>{t('Odustani', 'Cancel')}</Btn>
                        <Btn variant="primary" disabled={busy || !receive.primka_date || !receive.primka_no.trim()} onClick={() => void doReceive()}>{t('Zaprimi', 'Receive')}</Btn>
                    </div>
                </Modal>
            ) : null}

            {settingsOpen && meta?.is_superadmin ? (
                <Modal title={t('Postavke pristiglih pošiljaka', 'Inbound shipment settings')} onClose={() => setSettingsOpen(false)}>
                    <Field label={t('Prag, radnih dana u sefu', 'Threshold, working days in safe')}>
                        <input style={inp} type="number" min={1} max={60} value={sDraft.threshold_days} onChange={(e) => setSDraft({ ...sDraft, threshold_days: e.target.value })} />
                    </Field>
                    <Field label={t('Primatelji dnevnog sažetka (e-mail, odvojeno zarezom)', 'Daily digest recipients (comma separated)')}>
                        <textarea style={{ ...inp, minHeight: 50 }} value={sDraft.recipients} onChange={(e) => setSDraft({ ...sDraft, recipients: e.target.value })} />
                    </Field>
                    <Field label={t('Dostavljači (odvojeno zarezom)', 'Carriers (comma separated)')}>
                        <input style={inp} value={sDraft.carriers} onChange={(e) => setSDraft({ ...sDraft, carriers: e.target.value })} />
                    </Field>
                    {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Btn onClick={() => setSettingsOpen(false)}>{t('Odustani', 'Cancel')}</Btn>
                        <Btn variant="primary" disabled={busy} onClick={() => void saveSettings()}>{t('Spremi', 'Save')}</Btn>
                    </div>
                </Modal>
            ) : null}
        </div>
    );
}
